import React, { useRef, useEffect, useState, useCallback } from "react";
import { Card, Image, Modal, Button } from "react-bootstrap";
import { historyData } from "../data/historyData";
import "./History.css";
import PdfFlipBook from "../components/PdfFlipBook";

const MODAL_INITIAL = { show: false, url: "" };
const CARD_W        = 300;   // CSS --ht-card-w と合わせる
const CARD_GAP      = 48;    // CSS --ht-card-gap と合わせる
const AUTO_SPEED    = 1.2;   // px / frame

// ─── 背景画像リスト（パスを実際の画像に変更してください）────────
const BG_IMAGES = [
  "/images/bg_history_01.jpg",
  "/images/bg_history_02.jpg",
  "/images/bg_history_03.jpg",
  "/images/bg_history_04.jpg",
  "/images/bg_history_05.jpg",
];

// ─── スマホ判定 ──────────────────────────────────────────────────
const isMobile = () => window.innerWidth <= 768;

// ─── 1カード ────────────────────────────────────────────────────
const HistoryCard = ({ item, openModal }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // スマホは window root で縦スクロール検知、PCはトラック内
    const root = isMobile() ? null : el.closest(".history-track");
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); io.disconnect(); } },
      { root, threshold: 0.1 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={`history-card-wrap${visible ? " is-visible" : ""}`}>
      <div className="history-year">{item.year}</div>
      <div className="history-dot-wrap"><div className="history-dot" /></div>

      {/* カード：上段テキスト・下段画像で構造を固定 */}
      <Card className="hc-card">
        <Card.Body className="hc-card-body">
          {/* 上段：テキスト（flex-grow で残り全部使う） */}
          <div className="hc-text-area">
            <Card.Text>{item.text}</Card.Text>
          </div>

          {/* 下段：画像エリア（高さ固定・常に確保） */}
          <div className="hc-image-area">
            {item.img ? (
              <Image
                src={item.img}
                alt={item.pdf ? `${item.year}のカタログを開く` : item.year}
                className={`history-card-img ${item.pdf ? "history-card-img--clickable" : "history-card-img--disabled"}`}
                role={item.pdf ? "button" : undefined}
                tabIndex={item.pdf ? 0 : undefined}
                onClick={() => item.pdf && openModal(item.pdf)}
                onKeyDown={(e) => { if (item.pdf && (e.key === "Enter" || e.key === " ")) openModal(item.pdf); }}
              />
            ) : (
              /* 画像なしでも高さを保つ空プレースホルダー */
              <div className="hc-image-placeholder" />
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

// ─── メイン ─────────────────────────────────────────────────────
const HistoryTimeline = () => {
  const trackRef    = useRef(null);
  const progressRef = useRef(null);
  const bgLayerRef  = useRef(null);
  const pausedRef   = useRef(false);
  const lastBgRef   = useRef("");
  const [modal, setModal] = useState(MODAL_INITIAL);

  const openModal  = useCallback((p) => p && setModal({ show: true, url: p }), []);
  const closeModal = useCallback(() => setModal(MODAL_INITIAL), []);

  // ─── メインRAFループ（PCの横自動スクロール・背景・進捗）────────
  useEffect(() => {
    const track    = trackRef.current;
    const progress = progressRef.current;
    const bgLayer  = bgLayerRef.current;
    if (!track || !progress || !bgLayer) return;

    const updateUI = () => {
      const max = track.scrollWidth - track.clientWidth;
      progress.style.width = max > 0 ? `${(track.scrollLeft / max) * 100}%` : "0%";

      const cardIdx = Math.min(Math.round(track.scrollLeft / (CARD_W + CARD_GAP)), historyData.length - 1);
      const bgIdx   = BG_IMAGES.length > 0
        ? Math.floor((cardIdx / historyData.length) * BG_IMAGES.length) % BG_IMAGES.length
        : 0;
      const newBg = BG_IMAGES[bgIdx] || "";
      if (newBg !== lastBgRef.current) {
        lastBgRef.current             = newBg;
        bgLayer.style.transition      = "none";
        bgLayer.style.opacity         = "0";
        bgLayer.style.backgroundImage = newBg ? `url(${newBg})` : "none";
        requestAnimationFrame(() => {
          bgLayer.style.transition = "opacity 1.2s ease";
          bgLayer.style.opacity    = "1";
        });
      }
    };

    let rafId;
    const loop = () => {
      // スマホは自動スクロール不要（縦スクロールなので）
      if (!document.body.classList.contains("modal-open") && !isMobile()) {
        if (!pausedRef.current) {
          track.scrollLeft += AUTO_SPEED;
          if (track.scrollLeft >= track.scrollWidth - track.clientWidth - 1) {
            track.scrollLeft = 0;
          }
        }
        updateUI();
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // ─── ホイール → 手動横スクロール（PC・hover中のみ）─────────────
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onWheel = (e) => {
      if (document.body.classList.contains("modal-open")) return;
      if (isMobile()) return;            // スマホはデフォルト縦スクロールに任せる
      if (!pausedRef.current) return;    // hover中でなければ何もしない
      e.preventDefault();
      e.stopPropagation();
      const delta = Math.abs(e.deltaY) > Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      track.scrollLeft += delta * 1.2;
    };
    track.addEventListener("wheel", onWheel, { passive: false });
    return () => track.removeEventListener("wheel", onWheel);
  }, []);

  return (
    <div className="history-outer">
      <div ref={bgLayerRef} className="history-bg-layer" />
      <div className="history-bg-overlay" />

      <div className="history-title-block">
        <span className="history-title-label">沿革</span>
        <span className="history-title-en">Company History</span>
      </div>

      {/* 左右フェードマスク：トラックの外に置き overflow に切られない */}
      <div className="history-fade-left"  aria-hidden="true" />
      <div className="history-fade-right" aria-hidden="true" />

      <div
        className="history-track"
        ref={trackRef}
        onMouseEnter={() => { if (!isMobile()) pausedRef.current = true; }}
        onMouseLeave={() => { if (!isMobile()) pausedRef.current = false; }}
      >

        <div className="history-timeline-line" />
        <div className="history-cards-row">
          {historyData.map((item, i) => (
            <HistoryCard key={item.year} item={item} index={i} openModal={openModal} />
          ))}
          <div className="history-end-spacer" />
        </div>
      </div>

      <div className="history-progress-bar">
        <div ref={progressRef} className="history-progress-fill" />
      </div>

      <Modal show={modal.show} onHide={closeModal} centered dialogClassName="pdf-modal-dialog">
        <Modal.Header closeButton><Modal.Title>カタログ</Modal.Title></Modal.Header>
        <Modal.Body className="pdf-modal-body">
          {modal.url ? <PdfFlipBook pdfUrl={modal.url} /> : "PDFを読み込めませんでした"}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>閉じる</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default HistoryTimeline;
